import { HttpErrorResponse } from '@angular/common/http';
import { HttpStatusCodeEnum } from './Enum/HttpstatusCodeEnum';

/**
 * RailgunErrorMessage
 * エラーメッセージ定義
 * HACK: ココじゃない方がいいかもしれない
 */
export class RailgunErrorMessage{

	// ログインエラー（認証エラー）
	public static readonly ERR_MESSAGE_LOGIN_FAIL	= 'ログインできませんでした。入力内容を確認してください。';

	// リトライ可能エラー
	public static readonly ERR_MESSAGE_RETRY_OK		= 'エラーが発生しました。インターネットの接続を確認して、やりなおしてください。';
	// 認証エラー（これはAPIのトークン認証エラー用）
	public static readonly ERR_MESSAGE_AUTH_FAIL	=  'ユーザー認証できません。再度ログインしなおしてください。';
	// データなし
	public static readonly ERR_MESSAGE_NOT_FOUND	= '指定データがありません。';
	// 送信データが正しくない
	public static readonly ERR_MESSAGE_SRV_NONVALID	= ''; // サーバ側のエラーを表示する。

	// ファイル読み込みエラー
	public static readonly ERR_MESSAGE_NOT_FILEREAD = 'ファイルが読み込めませんでした。ファイルを確認してください。';

	// 汎用エラー
	public static readonly ERR__MESSAGE_OTHER		=  'エラーが発生しました。アプリを再表示してください。';

	////////////////////////////////////////////
	// 一般用
	// リロードエラー
	public static readonly ERR__MESSAGE_RELOAD		=  'エラーが発生しました。アプリを再表示します。';
}

/*

「エラーハンドリングの方針」

サーバー通信
	レスポンスが返ってこない
		タイムアウト。経路エラー。ブラウザエラー。OSエラーなど
	HTTPレベルのエラーレスポンスの受信
		https://developer.mozilla.org/ja/docs/Web/HTTP/Status
		200以外のHttpStatusCode
		400 Bad Request		アプリのバグ
		401 Unauthorized	未認証(トークンがない)
		403 Forbidden		認証エラー（トークンがダメ）
		404 Not Found		アプリかサーバのバグ。URLがない

		500 Internal Server Error	サーバのバグ。
		502 Bad Gateway				サーバのバグか設定ミス。
		503 Service Unavailable		サーバのバグか設定ミス。
	レスポンス内容エラー
		中身がない。パースに失敗。	アプリかサーバのバグ

	サーバ通信のエラー処理まとめ
		エラー表示してOKもらってリトライ	retryPossibility===true
			レスポンスが返ってこない
		エラー表示してログインへ遷移		unauthorized===true
			HTTPStatusCodeが401/403
		エラー表示して終わり				retryPossibility===false
			上記以外

アプリ内ロジック
		エラー表示して終わり

*/

/**
 * RailgunErrorFactory
 * エラー処理用のRailgunErrorを作成する
 */
export class RailgunErrorFactory {

	/**
	 * createRailgunErrorObj
	 * @param errorInst エラーが発生した時にハンドリングしたエラーオブジェクト。なければエラー文字列入れる。
	 * @param msgHead メッセージヘッダ。[Internal Error:] とかを指定。
	 */
	public static createRailgunErrorObj(
		errorInst: RailgunError | ErrorEvent | HttpErrorResponse | string,
		msgHead: string
	) {

		// 受け取ったエラーオブジェクトをRailgunErrorに変換して返す
		let obj = new RailgunError();

		// エラーのクラス毎にエラークラスのプロパティを設定していく
		switch (errorInst.constructor) {
			case RailgunError:
				obj = errorInst as RailgunError;
				// 自前のエラーの場合は、そのまま返す
				break;

			case ErrorEvent:
				// ネットワークとかブラウザのエラー等の場合
				obj.retryPossibility = true; // リトライ許可
				obj.rawMessage = msgHead + (errorInst as ErrorEvent).message;
				break;

			case HttpErrorResponse:
				// HTTPのエラーレスポンスを受信した場合
				// HTTPStatusCodeによって、エラー処理を決めるフラグを付ける
				obj.setHttpStatusFlag((errorInst as HttpErrorResponse).status);
				obj.rawMessage = msgHead + (errorInst as HttpErrorResponse).message;
				break;

			default:
				// エラークラスじゃなくてstringの場合
				// 内部エラーの場合などの汎用
				// その他エラーは、リトライ不許可
				obj.retryPossibility = false;
				obj.rawMessage = msgHead + errorInst;
				break;
		}

		// 作ったエラーオブジェクトを返す
		return obj;

	}
}

/**
 * RailgunError
 * エラークラス
 */
export class RailgunError implements Error {

	public name!: string;
	public message!: string; // 一般表示用メッセージ
	public rawMessage!: string; // デバッグログのメッセージ
	public respMessage!: string; // レスポンスのメッセージ
	public respBody!: string; // レスポンスのボディ
	public latestUrl!: string;
	public stack?: string;
	public other?: any;

	// HTTPStatusCode　ある時だけはいる。
	private httpStatus = 0; // Setterを使って入れる事

	// リトライしていいか
	public retryPossibility = false;
	// 認証エラーなのでログインに遷移すべき
	public unauthorized = false;
	// 指定されたデータがない
	public dataNotFound = false;
	// サーバ側のバリデーションエラー。エラーメッセージあり
	public saverValidationError = false;

	// ファイル読み込みエラー
	public fileReadError = false;


	/**
	 * getMessage
	 * エラーメッセージ取得
	 */
	public getMessage(): string {

		// リトライ可能エラー
		if(this.retryPossibility) {
			return RailgunErrorMessage.ERR_MESSAGE_RETRY_OK;
		}

		// 認証エラー
		if(this.unauthorized) {
			return RailgunErrorMessage.ERR_MESSAGE_AUTH_FAIL;
		}

		// データなしエラー
		if(this.dataNotFound) {
			return RailgunErrorMessage.ERR_MESSAGE_NOT_FOUND;
		}

		// サーバーバリデーションエラー。サーバー側のエラーを返す。
		if(this.saverValidationError) {
			return this.respBody;
		}

		// ファイル読み込みエラー
		if(this.fileReadError) {
			return RailgunErrorMessage.ERR_MESSAGE_NOT_FILEREAD;
		}

		// 汎用エラー
		return RailgunErrorMessage.ERR__MESSAGE_OTHER;

	}

	/**
	 * setHttpStatusFlag
	 * HTTPStatusCodeから、エラー処理を決めるフラグをセットする。
	 * @param status HttpStatusをいれる
	 */
	public setHttpStatusFlag(status: any) {

		this.httpStatus = Number(status);

		// ステータス毎にフラグ制御
		switch (this.httpStatus) {
			case HttpStatusCodeEnum.UNAUTHORIZED:
			case HttpStatusCodeEnum.FORBIDDEN:
				// 認証されてない
				this.unauthorized = true;
				break;
			case HttpStatusCodeEnum.NOT_FOUND:
				// 指定データなし
				this.dataNotFound = true;
				break;
			case HttpStatusCodeEnum.NOT_ACCEPTABLE:
				// サーバ側エラーメッセージあり。
				this.saverValidationError = true;
				break;
			default:
				// 認証エラー以外は、リトライ不許可
				this.retryPossibility = false;
				break;
		}

	}
	public getHttpStatus() {
		return this.httpStatus;
	}


}


